import React from 'react';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import {
    AggregationAlgorithm,
    AverageCalculationVersion,
    ChallengeLeaderboardEntrySupportDto,
    ChallengeMemberDisplaySettingsUpdateRequestDto,
    ChallengeSubscriptionType,
    ChallengeTeamLeaderboardEntrySupportDto
} from '@api/ChallengeClient';
import {
    ChallengeLeaderboardEntry,
    ChallengeTeamLeaderboardEntry
} from './types';

const DEFAULT_PAGE = {
    content: [] as ChallengeLeaderboardEntry[],
    number: 0,
    size: 0,
    totalPages: 0,
    totalElements: 0
};

const DEFAULT_TEAM_PAGE = {
    content: [] as ChallengeTeamLeaderboardEntry[],
    number: 0,
    size: 0,
    totalPages: 0,
    totalElements: 0
};

function withRowId(
    entry: ChallengeLeaderboardEntrySupportDto
): ChallengeLeaderboardEntry {
    return {
        ...entry,
        id: entry.subscriptionId
    };
}

function withTeamRowId(
    entry: ChallengeTeamLeaderboardEntrySupportDto
): ChallengeTeamLeaderboardEntry {
    return {
        ...entry,
        id: entry.teamSubscriptionId
    };
}

export function useStore(challengeId: number) {
    const { challengeClient } = injectTSDI(Api);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0,
        sort: 'position'
    });

    const fetchLeaderboardEntries = async (
        params?: QueryParamsDto,
        type?: ChallengeSubscriptionType
    ) => {
        const result =
            await challengeClient.support.challengeSupportControllerGetLeaderboardEntries(
                challengeId,
                { ...params, type }
            );

        return {
            ...result.data,
            content: (result.data.content || []).map(withRowId)
        };
    };

    const leaderboardEntries = useWrapRequest(
        (params?: QueryParamsDto) => fetchLeaderboardEntries(params),
        {
            defaultData: DEFAULT_PAGE
        }
    );

    const individualLeaderboardEntries = useWrapRequest(
        (params?: QueryParamsDto) =>
            fetchLeaderboardEntries(params, ChallengeSubscriptionType.MEMBER),
        {
            defaultData: DEFAULT_PAGE
        }
    );

    const teamLeaderboardEntries = useWrapRequest(
        async (params?: QueryParamsDto) => {
            const result =
                await challengeClient.support.challengeSupportControllerGetTeamLeaderboardEntries(
                    challengeId,
                    params
                );

            return {
                ...result.data,
                content: (result.data.content || []).map(withTeamRowId)
            };
        },
        {
            defaultData: DEFAULT_TEAM_PAGE
        }
    );

    const challengeDetails = useWrapRequest(async () => {
        const result =
            await challengeClient.support.challengeSupportControllerGetDetails(
                challengeId
            );

        return result.data;
    });

    const updateJoinDate = useWrapRequest(
        async (subscriptionId: number, createdDate: string) => {
            const result =
                await challengeClient.support.challengeSupportControllerUpdateMemberJoinDate(
                    subscriptionId,
                    { createdDate }
                );

            return withRowId(result.data);
        },
        {
            successMessage: () => 'Join timestamp updated'
        }
    );

    const updateDisplaySettings = useWrapRequest(
        async (
            subscriptionId: number,
            settings: ChallengeMemberDisplaySettingsUpdateRequestDto
        ) => {
            const result =
                await challengeClient.support.challengeSupportControllerUpdateMemberDisplaySettings(
                    subscriptionId,
                    settings
                );

            return withRowId(result.data);
        },
        {
            successMessage: () => 'Display settings updated'
        }
    );

    const updateResult = useWrapRequest(
        async (subscriptionId: number, value: number, day?: string) => {
            const result =
                await challengeClient.support.challengeSupportControllerUpdateMemberResult(
                    subscriptionId,
                    { value, day }
                );

            return withRowId(result.data);
        },
        {
            successMessage: () => 'Result updated'
        }
    );

    const teamDailyResults = useWrapRequest(
        async (teamSubscriptionId: number) => {
            const result =
                await challengeClient.support.challengeSupportControllerGetTeamDailyResults(
                    teamSubscriptionId
                );

            return result.data;
        }
    );

    const updateTeamDailyResults = useWrapRequest(
        async (
            teamSubscriptionId: number,
            days: { day: string; value: number; memberCount: number }[]
        ) => {
            const result =
                await challengeClient.support.challengeSupportControllerUpdateTeamDailyResults(
                    teamSubscriptionId,
                    { days }
                );

            return result.data;
        },
        {
            successMessage: () => 'Daily results updated'
        }
    );

    const teamMemberResults = useWrapRequest(
        async (teamSubscriptionId: number) => {
            const result =
                await challengeClient.support.challengeSupportControllerGetTeamMemberResults(
                    teamSubscriptionId
                );

            return result.data;
        }
    );

    const updateTeamMemberResults = useWrapRequest(
        async (
            teamSubscriptionId: number,
            members: { subscriptionId: number; value: number }[]
        ) => {
            const result =
                await challengeClient.support.challengeSupportControllerUpdateTeamMemberResults(
                    teamSubscriptionId,
                    { members }
                );

            return result.data;
        },
        {
            successMessage: () => 'Team member results updated'
        }
    );

    const addEntry = useWrapRequest(
        async (
            userId: string,
            value: number,
            day?: string,
            type?: ChallengeSubscriptionType
        ) => {
            const result =
                await challengeClient.support.challengeSupportControllerAddLeaderboardEntry(
                    challengeId,
                    { userId, value, day, type }
                );

            return withRowId(result.data);
        },
        {
            successMessage: () => 'Participant added'
        }
    );

    const removeEntry = useWrapRequest(
        async (subscriptionId: number) =>
            challengeClient.support.challengeSupportControllerRemoveLeaderboardEntry(
                subscriptionId
            ),
        {
            successMessage: () => 'Participant removed'
        }
    );

    const removeTeamEntry = useWrapRequest(
        async (teamSubscriptionId: number) =>
            challengeClient.support.challengeSupportControllerRemoveTeamLeaderboardEntry(
                teamSubscriptionId
            ),
        {
            successMessage: () => 'Team removed'
        }
    );

    const teamsEnabled = challengeDetails.$?.teamsLeaderboardEnabled ?? false;
    const individualLeaderboardEnabled =
        teamsEnabled &&
        (challengeDetails.$?.rule.personalRuleSet?.leaderboardEnabled ?? false);

    React.useEffect(() => {
        leaderboardEntries.request([searchParams]);
    }, [searchParams]);

    React.useEffect(() => {
        if (teamsEnabled) {
            teamLeaderboardEntries.request([searchParams]);
        }
    }, [searchParams, teamsEnabled]);

    React.useEffect(() => {
        if (individualLeaderboardEnabled) {
            individualLeaderboardEntries.request([searchParams]);
        }
    }, [searchParams, individualLeaderboardEnabled]);

    React.useEffect(() => {
        challengeDetails.request([]);
    }, [challengeId]);

    const refresh = React.useCallback(() => {
        leaderboardEntries.request([searchParams]);
        if (teamsEnabled) {
            teamLeaderboardEntries.request([searchParams]);
        }
        if (individualLeaderboardEnabled) {
            individualLeaderboardEntries.request([searchParams]);
        }
    }, [
        leaderboardEntries,
        teamLeaderboardEntries,
        individualLeaderboardEntries,
        searchParams,
        teamsEnabled,
        individualLeaderboardEnabled
    ]);

    return {
        leaderboardEntries,
        teamLeaderboardEntries,
        individualLeaderboardEntries,
        challengeDetails,
        teamsEnabled,
        individualLeaderboardEnabled,
        dateTimeRange: challengeDetails.$?.rule.dateTimeRange,
        dailyAverageTeams:
            challengeDetails.$?.rule.teamRuleSet?.pointsAlgorithm ===
                AggregationAlgorithm.AVERAGE &&
            challengeDetails.$?.rule.teamRuleSet?.averageCalculationVersion ===
                AverageCalculationVersion.V2,
        sumTeams:
            challengeDetails.$?.rule.teamRuleSet?.pointsAlgorithm ===
            AggregationAlgorithm.SUM,
        resultFormat: {
            fitnessMetricType: challengeDetails.$?.rule.fitnessMetricType,
            ruleType: challengeDetails.$?.rule.ruleType,
            aggregationType: challengeDetails.$?.rule.aggregationType,
            displayNormalizationFormula:
                challengeDetails.$?.rule.displayNormalizationFormula
        },
        updateJoinDate,
        updateResult,
        updateDisplaySettings,
        teamDailyResults,
        updateTeamDailyResults,
        teamMemberResults,
        updateTeamMemberResults,
        addEntry,
        removeEntry,
        removeTeamEntry,
        searchParams,
        dispatch,
        refresh
    };
}
